/* eslint react/jsx-max-depth: ["error", {max: 6}] -- header layout (icon + caption row + title) requires structural nesting */
import type { PropsWithChildren, ReactNode } from "react";

import { FAIconName, Icon, RichText, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface BaseActiveOnboardingCardContentProps extends PropsWithChildren<ViewWithTestID> {
  action?: ReactNode;
  caption: string;
  captionColor?: ColorToken;
  description?: string;
  duration?: string;
  icon: ReactNode;
  title: string;
}

const BaseActiveOnboardingCardContent = ({
  action,
  caption,
  captionColor = ColorToken.Primary100,
  children,
  description,
  duration,
  icon,
  testID,
  title,
}: BaseActiveOnboardingCardContentProps) => (
  <>
    <VStack gap={10}>
      <HStack alignItems="center" gap={12}>
        {icon}
        <Box flex={1}>
          <VStack gap={2}>
            <HStack alignItems="center" justifyContent="space-between">
              <Text
                color={captionColor}
                testID={joinTestIDs(testID, "caption")}
                variant="captionMedium"
              >
                {caption}
              </Text>
              {duration && (
                <HStack alignItems="center" gap={4}>
                  <Icon
                    color={ColorToken.AccentBlue}
                    name={FAIconName.Clock}
                    size="xs"
                    testID={joinTestIDs(testID, "clock")}
                  />
                  <Text
                    color={ColorToken.AccentBlue}
                    testID={joinTestIDs(testID, "duration")}
                    variant="overlineMedium"
                  >
                    {duration}
                  </Text>
                </HStack>
              )}
            </HStack>
            <Text
              color={ColorToken.NeutralPrimary}
              testID={joinTestIDs(testID, "title")}
              variant="h4"
            >
              {title}
            </Text>
          </VStack>
        </Box>
      </HStack>

      {description && (
        <RichText
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(testID, "description")}
          text={description}
          variant="caption"
        />
      )}

      {children}
    </VStack>

    {action && <Box marginTop={22}>{action}</Box>}
  </>
);

export type { BaseActiveOnboardingCardContentProps };
export { BaseActiveOnboardingCardContent };
